import {
    compareAsc,
    format,
    isValid,
    parse,
    subDays,
    differenceInBusinessDays,
    isMatch,
    isToday,
    isEqual,
} from 'date-fns';

export const formatDate = (dateString: string, dateFormat: string) =>
    format(
        new Date(dateString.replace(/-/g, '/').replace(/T.+/, '')),
        dateFormat
    );

export const isDateMatch = (dateString: string, dateFormat: string) =>
    isMatch(dateString, dateFormat);

export const getFormattedDate = (
    dateFormat: string,
    dateString?: string,
    shouldParse = false
) => {
    if (shouldParse && dateString) {
        return format(parse(dateString, 'P', new Date()), dateFormat);
    }
    if (dateString) {
        return format(new Date(dateString), dateFormat);
    }
    return format(new Date(), dateFormat);
};

export const getParsedDate = (dateString: any) =>
    parse(dateString, 'P', new Date());

export const isDateLesserThanToday = (
    date: any,
    shouldCompareEqual = false
) => {
    const currentDate = parse(
        format(new Date(), 'MM/dd/yyyy'),
        'P',
        new Date()
    );
    if (shouldCompareEqual) {
        return [0, -1].includes(compareAsc(date, currentDate));
    }
    return compareAsc(date, currentDate) === -1;
};

// Does not return the UTC timestamp of the given date;
// Gives wrong result for every input. Dead code right now — please fix before using.
export const getUTCDate = (date: string) => {
    const d = new Date(date);
    return Date.UTC(
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
        d.getSeconds()
    );
};

export const compareAscDates = (date1: any, date2: any) =>
    compareAsc(date1, date2);

export const isEndDateEarlierThanStartDate = (
    startDate: string,
    endDate: string
) => {
    const date1 = parse(startDate, 'P', new Date());
    const date2 = parse(endDate, 'P', new Date());
    if (
        startDate.length === 10 &&
        endDate.length === 10 &&
        isValid(date1) &&
        isValid(date2) &&
        compareAsc(date2, date1) === -1
    ) {
        return true;
    }
    return false;
};

export const formatDueDate = (date: string) =>
    `${format(new Date(date), 'yyyy-MM-dd')}T23:59:59Z`;

export const getDateBySubtractingDays = (
    dateString: string,
    daysToSubtract: number
) => {
    const date = parse(dateString, 'P', new Date());
    return format(subDays(date, daysToSubtract), 'yyyy-MM-dd');
};

// generic function to compare any two dates
export const compareDates = (date1: string, date2: string) => {
    const utcDate1 = getUTCDate(date1);
    const utcDate2 = getUTCDate(date2);
    if (utcDate1 <= utcDate2) return true;
    return false;
};

export const isDateDistanceGreaterThanNowByGivenDays = (
    dateString: string,
    noOfDays: number
) => {
    const now = new Date();
    const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
    const dateDistance = differenceInBusinessDays(parsedDate, now);
    return dateDistance >= noOfDays;
};

export const getLocalTodaysDate = (dateFormat: string) =>
    format(new Date(), dateFormat);

export const getTodaysUtcDate = () => new Date().toISOString().split('T')[0];

// Returns false for every date behind UTC (the Americas);
// Parses as UTC midnight, compares locally. Dead code right now — Please fix before using.
export const isDateTodays = (dateString: string) =>
    isToday(new Date(formatDate(dateString, 'yyyy-MM-dd')));

export const areDatesEqual = (date1: string, date2: string) =>
    isEqual(
        new Date(formatDate(date1, 'MM/dd/yyyy')),
        new Date(formatDate(date2, 'MM/dd/yyyy'))
    );
export const isTokenExpired = (expiredTime: number) => {
    const expiredDate = new Date(expiredTime);
    const currentDate = new Date();
    const diff = expiredDate.getTime() - currentDate.getTime();
    return Math.round(diff / 60000) < 11;
};

export const sortByDate = (arr: any, key: string, inAsc?: boolean) =>
    arr.sort((a: any, b: any) => {
        const dateA: any = new Date(a[key]);
        const dateB: any = new Date(b[key]);
        return inAsc ? dateA - dateB : dateB - dateA;
    });
